import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { RefreshKids } from 'education-icons';
import { Scale, Button, Keyboard, RoundedPlate, Switch, Text, LegoTooltip as Tooltip } from 'platform-components';
import { getMe } from 'platform-components/src/components/user/selectors';
import { isTouchDevice } from 'platform-components/src/utils/device';
import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import { accessibleClick } from '@edu-frontend/modules/src/shared/lib/a11y';

import { PROBLEM_RUN_PROBLEM_WIDTH } from 'common.components/problem-run/constants';

import './problem-run__bar.scss';

interface Props {
    shouldShowCorrectAnswers: boolean;
    toggleProblemCorrectAnswer: (shouldShowCorrectAnswers: boolean) => void;
    shouldShowAnswerButton: boolean;
    disabled: boolean;
    handleAnswer: () => void;
    answerLoadingStatus: LOADING_STATUSES;
    isAnswered: boolean;
    handleRefresh: () => void;
    shouldShowProblemBar: boolean;
}

export const ProblemRunBar = ({
    shouldShowCorrectAnswers,
    toggleProblemCorrectAnswer,
    shouldShowAnswerButton,
    disabled,
    handleAnswer,
    answerLoadingStatus,
    isAnswered,
    handleRefresh,
    shouldShowProblemBar,
}: Props) => {
    const [isTooltipVisible, setTooltipVisible] = useState(false);

    const ref = useRef<HTMLDivElement>(null);
    const me = useSelector(getMe);
    const isTeacher = me?.is_teacher;

    const handleSwitchChange = useCallback(
        () => toggleProblemCorrectAnswer(!shouldShowCorrectAnswers),
        [toggleProblemCorrectAnswer, shouldShowCorrectAnswers]
    );

    return (
        <div className="problem-run__bar-wrapper">
            <Scale minWidth={PROBLEM_RUN_PROBLEM_WIDTH}>
                <Keyboard mix="problem-run__primary-keyboard" />
            </Scale>
            <div
                className="problem-run__bar"
                {...(isTouchDevice
                    ? {
                          onClick: () => setTooltipVisible(!isTooltipVisible),
                      }
                    : {
                          onMouseEnter: () => setTooltipVisible(true),
                          onMouseLeave: () => setTooltipVisible(false),
                      })}
            >
                <CSSTransition
                    classNames={'problem-run__bar-animation'}
                    in={shouldShowProblemBar}
                    mountOnEnter={true}
                    timeout={300}
                    unmountOnExit={true}
                >
                    <>
                        <RoundedPlate mix="problem-run__bar-panel" ref={ref}>
                            <Text id="lessonModal.header.showAnswersShort" size={Text.SIZE.L} weight={Text.WEIGHT.M} />
                            <Switch
                                checked={shouldShowCorrectAnswers}
                                disabled={!isTeacher}
                                onChange={handleSwitchChange}
                                size={Switch.SIZES.M}
                                theme={Switch.THEMES.GREEN}
                                view={Switch.VIEWS.INVERSE}
                            />
                            {shouldShowAnswerButton && (
                                <>
                                    <div className="problem-run__bar-controls-divider" />
                                    <Button
                                        className="problem-run__bar-answer-button"
                                        disabled={disabled}
                                        onClick={handleAnswer}
                                        progress={answerLoadingStatus === LOADING_STATUSES.LOADING}
                                        rounded
                                        size={Button.SIZE.L}
                                    >
                                        <Text
                                            id="lessonView.problem.setAnswer"
                                            size={Text.SIZE.L}
                                            weight={Text.WEIGHT.M}
                                        />
                                    </Button>
                                </>
                            )}
                        </RoundedPlate>
                        {isAnswered && (
                            <div className="problem-run__bar-refresh-attempts" {...accessibleClick(handleRefresh)}>
                                <RefreshKids />
                            </div>
                        )}
                        {!isTeacher && (
                            <Tooltip
                                anchor={ref}
                                className="problem-run__bar-tooltip"
                                direction={['top']}
                                hasTail
                                key="problem-bar-tooltip"
                                view="default"
                                visible={isTooltipVisible}
                            >
                                <Text id="problemRunBar.tooltip.text" />
                            </Tooltip>
                        )}
                    </>
                </CSSTransition>
            </div>
        </div>
    );
};
